import { GenerateDocumentResponse, GenerateQuittanceDto } from "@dune/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { privateAxios } from "../axios";
import { propositionsQueryKeys } from "../query-keys/propositions.query-keys";
import { receiptKeys } from "../queries/receipt.queries";

const updateReceiptDuneComment = async (
	receiptId: number,
	duneComment: string | null,
): Promise<void> => {
	await privateAxios.patch(`/receipts/${receiptId}/dune-comment`, {
		duneComment,
	});
};

export const useUpdateReceiptDuneComment = (receiptId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (duneComment: string | null) =>
			updateReceiptDuneComment(receiptId, duneComment),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: receiptKeys.detail(receiptId),
				refetchType: "active",
			});
		},
	});
};

const generateQuittanceDocument = async (
	propositionId: number,
	receiptId: string,
	data: GenerateQuittanceDto,
): Promise<GenerateDocumentResponse> => {
	const { data: response } = await privateAxios.post<GenerateDocumentResponse>(
		`/propositions/${propositionId}/generate-receipt`,
		{ receiptId, ...data },
	);
	return response;
};

export const useGenerateQuittance = (
	propositionId: number,
	receiptId: string,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: GenerateQuittanceDto) =>
			generateQuittanceDocument(propositionId, receiptId, data),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.detail(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.documents(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.tasks(propositionId),
					refetchType: "active",
				}),
			]);
		},
	});
};

export const useGenerateQuittanceWithReceiptId = (propositionId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: GenerateQuittanceDto & { receiptId: string }) =>
			generateQuittanceDocument(propositionId, data.receiptId, {
				templateId: data.templateId,
				signataireDuneId: data.signataireDuneId,
				format: data.format,
				skipTaskValidation: data.skipTaskValidation,
			}),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.detail(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.documents(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.tasks(propositionId),
					refetchType: "active",
				}),
			]);
		},
	});
};
